// Sound effects, synthesised at runtime.
//
// No audio files: a handful of oscillator blips cost nothing to ship, never
// 404, and can be tuned by changing a number instead of re-exporting a wav.

let context: AudioContext | null = null
let muted = readMuted()

function readMuted(): boolean {
  try {
    return localStorage.getItem('lf_muted') === '1'
  } catch {
    // Private browsing can throw on localStorage access.
    return false
  }
}

export function isMuted() {
  return muted
}

export function setMuted(value: boolean) {
  muted = value
  try {
    localStorage.setItem('lf_muted', value ? '1' : '0')
  } catch {
    // Not worth failing a click over.
  }
}

function ctx(): AudioContext | null {
  if (muted) return null
  // Created lazily on the first sound: browsers refuse to start an AudioContext
  // before a user gesture, and constructing one at import time leaves it stuck
  // in "suspended" forever.
  if (!context) {
    try {
      context = new AudioContext()
    } catch {
      return null
    }
  }
  if (context.state === 'suspended') void context.resume()
  return context
}

interface Blip {
  freq: number
  duration: number
  type?: OscillatorType
  volume?: number
  /** Frequency to glide to over the note's life. */
  glideTo?: number
}

function play(blips: Blip[], gap = 0) {
  const audio = ctx()
  if (!audio) return

  let at = audio.currentTime
  for (const blip of blips) {
    const osc = audio.createOscillator()
    const gain = audio.createGain()

    osc.type = blip.type ?? 'sine'
    osc.frequency.setValueAtTime(blip.freq, at)
    if (blip.glideTo) {
      osc.frequency.exponentialRampToValueAtTime(blip.glideTo, at + blip.duration)
    }

    // A short attack and an exponential release: a raw gate on a sine wave
    // clicks audibly at both ends.
    const peak = blip.volume ?? 0.12
    gain.gain.setValueAtTime(0.0001, at)
    gain.gain.exponentialRampToValueAtTime(peak, at + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + blip.duration)

    osc.connect(gain).connect(audio.destination)
    osc.start(at)
    osc.stop(at + blip.duration + 0.02)

    at += blip.duration + gap
  }
}

export const sfx = {
  correct: () => play([{ freq: 660, duration: 0.09 }, { freq: 880, duration: 0.13 }]),
  wrong: () =>
    play([{ freq: 220, duration: 0.22, type: 'sawtooth', volume: 0.08, glideTo: 150 }]),
  /** Rises with the combo, so a streak audibly builds. */
  combo: (streak: number) =>
    play([{ freq: 520 + Math.min(streak, 8) * 60, duration: 0.08, type: 'triangle' }]),
  complete: () =>
    play(
      [
        { freq: 523, duration: 0.12 },
        { freq: 659, duration: 0.12 },
        { freq: 784, duration: 0.12 },
        { freq: 1047, duration: 0.3 },
      ],
      0.02,
    ),
  crown: () =>
    play(
      [
        { freq: 784, duration: 0.1 },
        { freq: 1047, duration: 0.1 },
        { freq: 1319, duration: 0.35 },
      ],
      0.03,
    ),
  tick: () => play([{ freq: 400, duration: 0.04, volume: 0.05 }]),
  /** A dull thud for arcade collisions and game-over. */
  thud: () =>
    play([{ freq: 140, duration: 0.18, type: 'square', volume: 0.09, glideTo: 70 }]),
  pop: () => play([{ freq: 900, duration: 0.05, type: 'triangle', volume: 0.09 }]),
}

/** A metronome click for the sing-along beat. */
export function beat(accent = false) {
  play([{ freq: accent ? 1200 : 800, duration: 0.04, volume: accent ? 0.09 : 0.05 }])
}
